// HumanaIntegration.jsx - Humana API Integration Card
import React, { useState, useEffect } from 'react';
import {
  CheckCircle2,
  XCircle,
  RefreshCw,
  Users,
  FileText,
  Pill,
  Stethoscope,
  AlertTriangle,
  TrendingUp,
  Plug
} from 'lucide-react';
import api from '../api';

export default function HumanaIntegration() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/humana/status');
      setStatus(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch Humana status:', err);
      setStatus({ connected: false });
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async () => {
    try {
      const response = await api.get('/api/humana/connect');
      if (response.data.authUrl) {
        window.location.href = response.data.authUrl;
      }
    } catch (err) {
      console.error('Failed to get Humana auth URL:', err);
      setError('Failed to connect to Humana. Please try again.');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Are you sure you want to disconnect Humana? Your client data will remain, but syncing will stop.')) {
      return;
    }

    try {
      await api.post('/api/humana/disconnect');
      setStatus({ connected: false });
    } catch (err) {
      console.error('Failed to disconnect Humana:', err);
      setError('Failed to disconnect. Please try again.');
    }
  };

  const handleSync = async () => {
    try {
      setSyncing(true);
      await api.post('/api/humana/sync');
      // Refresh status after sync starts
      setTimeout(fetchStatus, 2000);
    } catch (err) {
      console.error('Failed to start sync:', err);
      setError('Failed to start sync. Please try again.');
    } finally {
      setSyncing(false);
    }
  };

  const isConnected = status?.connected;
  const isSyncing = status?.sync_status === 'syncing' || syncing;

  if (loading) {
    return (
      <div className="bg-white rounded-2xl shadow-lg p-8 border-2 border-gray-200">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="animate-spin text-[#00A9E0]" size={32} />
        </div>
      </div>
    );
  }

  return (
    <div data-tour="carrier-integrations" className={`bg-white rounded-2xl shadow-lg p-8 border-2 transition-all ${isConnected ? 'border-[#00A9E0]' : 'border-gray-200'}`}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 bg-gradient-to-br from-[#00A9E0] to-[#007CB0] rounded-2xl flex items-center justify-center">
            <span className="text-white font-bold text-xl">H</span>
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#172A3A] flex items-center gap-2">
              Humana Integration
              {isConnected && <CheckCircle2 className="text-green-600" size={20} />}
            </h3>
            <p className="text-gray-600 text-sm">Medicare client data & enrollment tracking</p>
          </div>
        </div>
        <div>
          {isConnected ? (
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-green-100 text-green-700 rounded-lg font-semibold text-sm">
              <CheckCircle2 size={16} />
              Connected
            </span>
          ) : (
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-semibold text-sm">
              <XCircle size={16} />
              Not Connected
            </span>
          )}
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      <div className="space-y-3 mb-6">
        <div className="flex items-start gap-3">
          <Users className="text-[#00A9E0] mt-1" size={18} />
          <div>
            <div className="font-semibold text-[#172A3A]">Book of Business</div>
            <div className="text-sm text-gray-600">
              Auto-import all your Medicare clients with plan details
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <FileText className="text-[#00A9E0] mt-1" size={18} />
          <div>
            <div className="font-semibold text-[#172A3A]">Enrollment Tracking</div>
            <div className="text-sm text-gray-600">
              Track new enrollments, rejections, and disenrollment requests
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Pill className="text-[#00A9E0] mt-1" size={18} />
          <div>
            <div className="font-semibold text-[#172A3A]">Drug Formulary</div>
            <div className="text-sm text-gray-600">
              Check medication tiers and prior auth requirements
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <Stethoscope className="text-[#00A9E0] mt-1" size={18} />
          <div>
            <div className="font-semibold text-[#172A3A]">Provider Network</div>
            <div className="text-sm text-gray-600">
              Verify if client's doctors are in-network
            </div>
          </div>
        </div>
        <div className="flex items-start gap-3">
          <TrendingUp className="text-[#00A9E0] mt-1" size={18} />
          <div>
            <div className="font-semibold text-[#172A3A]">Retention Analytics</div>
            <div className="text-sm text-gray-600">
              Track churn, saved clients, and win-backs
            </div>
          </div>
        </div>
      </div>

      {isConnected ? (
        <div className="space-y-4">
          {/* Sync Status */}
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm text-gray-500">Last synced</div>
                <div className="font-semibold text-[#172A3A]">
                  {status.last_sync_at
                    ? new Date(status.last_sync_at).toLocaleString()
                    : 'Never'}
                </div>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                isSyncing ? 'bg-yellow-100 text-yellow-700' :
                status.sync_status === 'complete' ? 'bg-green-100 text-green-700' :
                status.sync_status === 'error' ? 'bg-red-100 text-red-700' :
                'bg-gray-100 text-gray-600'
              }`}>
                {isSyncing ? 'Syncing...' :
                 status.sync_status === 'complete' ? 'Complete' :
                 status.sync_status === 'error' ? 'Error' :
                 'Pending'}
              </div>
            </div>
            {status.sync_error && (
              <div className="mt-2 text-sm text-red-600">
                {status.sync_error}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t">
            <button
              onClick={handleSync}
              disabled={isSyncing}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-all ${
                isSyncing
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-[#00A9E0] hover:bg-[#007CB0] text-white'
              }`}
            >
              <RefreshCw size={16} className={isSyncing ? 'animate-spin' : ''} />
              {isSyncing ? 'Syncing...' : 'Sync Now'}
            </button>
            <button
              onClick={handleDisconnect}
              className="bg-red-100 hover:bg-red-200 text-red-700 px-6 py-2 font-bold rounded-lg transition-all"
            >
              Disconnect
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={handleConnect}
          className="w-full bg-gradient-to-r from-[#00A9E0] to-[#007CB0] hover:from-[#007CB0] hover:to-[#005A87] text-white px-6 py-3 font-bold rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          <Plug size={20} />
          Connect Humana Account
        </button>
      )}

      {/* Info Banner */}
      {!isConnected && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-xl p-4">
          <h4 className="font-bold text-blue-900 mb-2">Why connect Humana?</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>✓ Automatically import all your Medicare clients</li>
            <li>✓ Get instant alerts when clients try to disenroll</li>
            <li>✓ Track medication formulary issues before they cause problems</li>
            <li>✓ Know immediately if a client's doctor leaves the network</li>
            <li>✓ Track your retention rate and clients you've saved</li>
          </ul>
        </div>
      )}
    </div>
  );
}
